import aiAssistedHtml from '@/content/blog/ai-assisted-nutrition-platform-professionals-uk.html?raw';
import clinicalPlatformsHtml from '@/content/blog/best-clinical-nutrition-platforms-uk-healthcare-practitioners.html?raw';
import clientManagementPlatformsHtml from '@/content/blog/client-management-platforms-for-nutritionists.html?raw';
import howToGetClientsHtml from '@/content/blog/how-to-get-clients-as-a-nutritionist-uk.html?raw';
import blogImage1 from '@/assets/images/landing/blog-1.svg';
import blogImage2 from '@/assets/images/landing/blog-2.svg';
import blogImage3 from '@/assets/images/landing/blog-3.svg';
import { BlogSeoMetadata, blogSeoMetadataBySlug } from './blogSeoMetadata';

export type BlogFaq = {
  question: string;
  answer: string;
};

export type BlogPost = BlogSeoMetadata & {
  image: string;
  contentHtml: string;
  faqs: BlogFaq[];
};

const howToGetClientsSeo = blogSeoMetadataBySlug['how-to-get-clients-as-a-nutritionist-uk'];
const clientManagementPlatformsSeo =
  blogSeoMetadataBySlug['client-management-platforms-for-nutritionists'];
const clinicalPlatformsSeo =
  blogSeoMetadataBySlug['best-clinical-nutrition-platforms-uk-healthcare-practitioners'];
const aiAssistedSeo = blogSeoMetadataBySlug['ai-assisted-nutrition-platform-professionals-uk'];

export const blogPosts: BlogPost[] = [
  {
    ...clientManagementPlatformsSeo,
    image: blogImage2,
    contentHtml: clientManagementPlatformsHtml,
    faqs: [
      {
        question: 'What is the best client management platform for nutritionists?',
        answer:
          "There isn't one answer for every practice. Telehealth-heavy practices often lean toward Healthie for its built-in video conferencing. Paperwork-heavy or NHS-adjacent practices often prefer NutriAdmin's EHR depth. Practices in India frequently choose Ntuitive or Zeefit for local food data and INR-friendly workflows. Practices that want AI to handle the bulk of meal-plan building, alongside client management, are the ones who tend to get the most out of a platform like CalorieScience.",
      },
      {
        question: 'What features should nutrition practice management software include?',
        answer:
          'At minimum: client records, scheduling, meal planning, a client portal, billing and progress tracking. What separates a modern platform from an older one is whether it also includes AI-assisted meal generation, automated intake, programme templates and automated follow-ups.',
      },
      {
        question: 'How is a client management platform different from a meal-planning app?',
        answer:
          'Meal-planning apps — That Clean Life, Cronometer Pro — focus narrowly on building and analysing plans. A client management platform covers the whole workflow: intake, scheduling, communication, billing and reporting, with meal planning built in rather than bolted on as a separate subscription.',
      },
      {
        question: 'Is CalorieScience HIPAA compliant?',
        answer:
          "CalorieScience is UK and EU GDPR compliant. HIPAA compliance is currently in progress. If you're US-based and HIPAA compliance is a hard requirement today, that's worth factoring into your decision now, alongside where CalorieScience is headed.",
      },
      {
        question: 'Can AI actually personalise a meal plan, or is it just a template?',
        answer:
          "On a well-built platform, AI handles the mechanical matching — calorie targets, allergens, preferences — while the practitioner reviews and approves every plan before a client sees it. The clinical judgement doesn't move; the hours spent on manual calculation do.",
      },
      {
        question: 'Is switching platforms worth it if my current system technically works?',
        answer:
          'If "works" means you\'re still rebuilding meal plans by hand, chasing clients for check-ins yourself, or running a second app for billing, the time saved by consolidating onto an AI-assisted platform usually pays for the migration hassle within a month or two.',
      },
    ],
  },
  {
    ...howToGetClientsSeo,
    image: blogImage1,
    contentHtml: howToGetClientsHtml,
    faqs: [
      {
        question: 'How do nutritionists get clients in the UK?',
        answer:
          'UK nutritionists get clients most reliably by combining a clearly defined niche, an optimised Google Business Profile, a website built to convert, a referral network with GPs and allied health practitioners, and a practice system that retains clients past the first few sessions. Marketing fills the pipeline; operations decide whether enquiries become paid programmes.',
      },
      {
        question: 'What is the best way to grow a nutrition practice in the UK?',
        answer:
          'The fastest sustainable growth comes from packaging services as named, outcome-defined programmes rather than hourly consultations, building one referral channel deeply rather than four shallowly, and removing the operational drag — meal-plan building, intake chasing, manual invoicing — that caps how many clients a practitioner can serve.',
      },
      {
        question: 'How can nutritionists retain clients?',
        answer:
          'Retention improves when clients see structured progress between sessions. The practical levers are pre-session intake, automated between-session check-ins, a defined programme arc with a clear end-point and progress artefact, and a follow-up offer introduced before the current programme finishes.',
      },
      {
        question: 'What software do UK nutritionists use?',
        answer:
          'UK nutritionists typically combine meal-planning tools, practice management tools, and general business tools. Newer all-in-one platforms like CalorieScience.ai consolidate intake, AI-assisted meal planning, programme delivery, follow-ups and invoicing into one UK-aligned workflow.',
      },
      {
        question: 'How can AI nutrition software help private-practice nutritionists?',
        answer:
          'AI nutrition software helps by automating the operational layer of a practice — generating personalised meal plans in minutes, structuring intake before sessions, sending between-session follow-ups, and producing client-facing progress reports. The clinical judgement stays with the practitioner; the repetitive work stops eating the week.',
      },
      {
        question: 'How much does practice management software cost for a UK nutritionist?',
        answer:
          'UK practice management platforms typically range from around £30 to £150 per month depending on the depth of features. CalorieScience founding-cohort pricing starts at £49 per month for Solo, £79 for Clinical and £149 for Clinic, and is locked in for the lifetime of the subscription.',
      },
      {
        question: 'Is “nutritionist” a protected title in the UK?',
        answer:
          'No. “Nutritionist” is not a protected title in the UK — anyone can use it. “Dietitian” is protected and regulated by the HCPC. UK clients increasingly look for credentials from the AfN, BANT, CNHC, or HCPC registration for dietitians.',
      },
      {
        question: 'Do clients need a GP referral to see a private nutritionist?',
        answer:
          'No. UK clients do not need a GP referral to book privately with a registered nutritionist, nutritional therapist or dietitian. Many do come on GP recommendation, but self-referral is the most common route into private practice.',
      },
    ],
  },
  {
    ...clinicalPlatformsSeo,
    image: blogImage2,
    contentHtml: clinicalPlatformsHtml,
    faqs: [
      {
        question: 'What is the best clinical nutrition platform for UK practitioners in 2026?',
        answer:
          'For the broadest range of UK practitioners, from solo dietitians and clinical nutritionists to GPs, endocrinologists, and nutrition educators, CalorieScience.ai is the strongest option in 2026. It combines AI-powered meal planning, biomarker-linked nutrition guidance, automated intake, complete client management, and integrated invoicing in one connected workflow.',
      },
      {
        question: 'What should I look for in clinical nutrition software?',
        answer:
          'The most important criteria are whether it handles the full practitioner workflow in one place, the depth of its nutrition analysis capability, whether it can incorporate biomarker data, whether AI-assisted plan generation is genuinely personalised rather than template-based, and whether it supports GDPR compliance for UK client data.',
      },
      {
        question: 'Is there an AI-assisted platform for nutrition practitioners in the UK?',
        answer:
          'Yes. CalorieScience.ai is an advanced AI-assisted platform for nutrition practitioners in the UK. Its AI generates personalised meal plans from clinical data including biomarkers, conditions, food preferences, cultural context, and lifestyle factors in minutes rather than hours.',
      },
      {
        question: 'How does CalorieScience.ai compare to Practice Better and NutriAdmin?',
        answer:
          'CalorieScience.ai leads on clinical depth, specifically biomarker integration, AI meal planning quality, and workflow automation. Practice Better is stronger for wellness-orientated health coaches, while NutriAdmin is an accessible UK entry-level option. For evidence-based clinical nutrition, CalorieScience.ai is built for a deeper level of clinical rigour.',
      },
      {
        question: 'Can GPs and endocrinologists use a nutrition platform like CalorieScience.ai?',
        answer:
          'Yes. CalorieScience.ai is built for any practitioner who uses food as part of a clinical intervention, including GPs, endocrinologists, psychologists, dietitians, nutritionists, fitness coaches, and nutrition educators.',
      },
      {
        question: 'Is CalorieScience.ai UK and EU GDPR compliant?',
        answer:
          'Yes. CalorieScience.ai is UK and EU GDPR compliant, with HIPAA in progress. Several US-based competitors require careful review of their data residency and processing agreements before UK adoption.',
      },
    ],
  },
  {
    ...aiAssistedSeo,
    image: blogImage3,
    contentHtml: aiAssistedHtml,
    faqs: [
      {
        question: 'Will AI-generated meal plans be personalised enough for my clients?',
        answer:
          'The quality of personalisation depends on the quality of the platform. A platform like CalorieScience.ai builds from detailed clinical data including biomarkers, diagnosed conditions, medication context, and cultural food preferences. You still review and refine every plan before it reaches a client.',
      },
      {
        question: 'How much time does switching to an AI platform actually save?',
        answer:
          'For a practitioner managing twenty or more active clients, the realistic saving is eight to twelve hours per week across meal planning, intake admin, invoicing, and follow-up communications. Smaller caseloads save proportionally less, but gain the infrastructure to grow.',
      },
      {
        question: 'Is AI nutrition software GDPR compliant for UK practitioners?',
        answer:
          'Not automatically. UK practitioners handling patient data must check data residency, data processing agreements, and documented security practices. CalorieScience.ai is UK and EU GDPR compliant, with HIPAA in progress.',
      },
      {
        question: 'Does using AI meal planning software mean my plans are less bespoke?',
        answer:
          'Only if the platform relies on template-based generation. Genuine AI meal planning software builds from clinical inputs and creates a personalised first draft, which the practitioner reviews and refines.',
      },
      {
        question: 'What happens to my current client data when I switch platforms?',
        answer:
          'This depends on the platform you are moving from and the one you are moving to. Most established platforms support export in standard formats, and CalorieScience.ai supports a free trial so the transition can be planned before committing.',
      },
    ],
  },
];

export const blogPostsBySlug = blogPosts.reduce<Record<string, BlogPost>>((postsBySlug, post) => {
  postsBySlug[post.slug] = post;
  return postsBySlug;
}, {});
